import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { useContext, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { A11y, Autoplay, EffectFade, Keyboard, Pagination } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import { GetKOHOBtn } from 'components/rebrand/GetKOHOBtn';
import { LocaleContext } from 'components/rebrand/Layout';
import { SwiperCarousel } from 'components/rebrand/SwiperCarousel';

import { IHeroCarouselSlide, IVideoAsset } from 'typescriptDefs/pages/home';

import { useIsMobileSize } from 'helpers';
import { afTrackingByPage } from 'helpers/getAfTracking';
import { checkIfSafari } from 'utils/check-if-safari';

const HeroSlide = ({
  animationDone,
  index,
  isActive,
  isSafari,
  slide: { content, videoMp4, videoWebm },
}: {
  animationDone: boolean;
  index: number;
  isActive: boolean;
  isSafari: boolean | null;
  slide: IHeroCarouselSlide;
}) => {
  return (
    <div className="grid gap-0 md:grid-cols-5 md:grid-rows-1 md:gap-50">
      <div
        className={twMerge(
          clsx(
            'col-start-1 row-start-2 mx-auto text-white min-[380px]:w-4/5 md:col-span-2 md:row-start-1 md:my-auto md:w-full [&>*]:text-center md:[&>*]:text-left',
            '[&_h2:last-of-type]:mb-12 md:[&_h2:last-of-type]:mb-24 [&_h2]:text-secondary-75 [&_h2_strong]:text-secondary-50',
          ),
        )}
      >
        <CustomPortableText content={content} />
      </div>
      <div className="col-start-1 row-start-1 md:col-span-3 md:col-start-3">
        {(videoMp4 || videoWebm) && isSafari !== null && animationDone && isActive ? (
          <video
            id={`animation-index-${index}`}
            playsInline
            webkit-playsinline="true"
            className="mx-auto !h-368 !object-cover object-center md:!h-490"
            muted
            autoPlay
            loop={index === 0}
          >
            {videoMp4 && isSafari && <source src={videoMp4.asset.url} type="video/mp4" />}
            {videoWebm && !isSafari && <source src={videoWebm.asset.url} type="video/webm" />}
          </video>
        ) : (
          <div className="mx-auto !h-368 md:!h-490" />
        )}
      </div>
    </div>
  );
};

const HeroCarousel = ({
  animationDone,
  carousel,
  className,
  isSafari,
}: {
  animationDone: boolean;
  carousel: IHeroCarouselSlide[];
  className?: string;
  isSafari: boolean | null;
}) => {
  const paginationClass = 'homeHeroCarouselPagination';
  const [activeSlide, setActiveSlide] = useState(0);
  return (
    <div className={twMerge('md:ml-80', className)}>
      <SwiperCarousel
        noNavigation
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={800}
        breakpoints={{
          0: { slidesPerView: 1, spaceBetween: 0 },
        }}
        keyboard
        modules={[A11y, Autoplay, EffectFade, Keyboard, Pagination]}
        pagination={{
          clickable: true,
          el: `.${paginationClass}`,
        }}
        slidesOffsetAfter={0}
        watchSlidesProgress
        onActiveIndexChange={(x) => {
          setActiveSlide(x.activeIndex);
        }}
        autoplay={{
          disableOnInteraction: false,
        }}
      >
        {carousel.map((slide, index) => (
          <SwiperSlide key={index} data-swiper-autoplay={index === 0 ? 9000 : 4500}>
            <HeroSlide
              index={index}
              isSafari={isSafari}
              slide={slide}
              animationDone={animationDone}
              isActive={index === activeSlide}
            />
          </SwiperSlide>
        ))}
      </SwiperCarousel>
      <div
        className={twMerge(
          clsx(
            'homeHeroCarouselPagination relative z-2 mx-auto mt-48 flex !w-fit flex-row items-center gap-12 rounded-full bg-white bg-opacity-10 p-8 transition-all duration-[0.3s] md:mt-56',
            '[&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:h-12 [&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:w-12 [&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:bg-secondary-75',
            '[&_.swiper-pagination-bullet:hover]:bg-secondary-75 [&_.swiper-pagination-bullet]:!mx-0 [&_.swiper-pagination-bullet]:bg-white [&_.swiper-pagination-bullet]:opacity-100',
          ),
        )}
      />
    </div>
  );
};

type Props = {
  animationMp4: IVideoAsset | null;
  animationWebm: IVideoAsset | null;
  animationMp4Mobile: IVideoAsset | null;
  animationWebmMobile: IVideoAsset | null;
  carousel: IHeroCarouselSlide[];
  ctaText: string;
  heading: PortableTextBlock | PortableTextBlock[];
};

const Hero = ({
  animationMp4,
  animationWebm,
  animationMp4Mobile,
  animationWebmMobile,
  carousel,
  ctaText,
  heading,
}: Props) => {
  const locale = useContext(LocaleContext);
  const [animationState, setAnimationState] = useState<'notStarted' | 'playing' | 'stopped'>('notStarted');
  const trackingParams = afTrackingByPage('HOME');
  const [isSafari, setIsSafari] = useState<boolean | null>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const isMobile = useIsMobileSize();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (window !== undefined) setIsSafari(checkIfSafari(window.navigator.userAgent));
  }, []);

  const startAnimation = () => setAnimationState('playing');
  const stopAnimation = () => setAnimationState('stopped');

  const { ref, inView } = useInView({
    threshold: 0.8,
    trackVisibility: true,
    triggerOnce: true,
    delay: 100,
    fallbackInView: true,
    onChange: (inView) => {
      if (inView && animationState === 'notStarted') {
        (document.getElementById('cardAnimation') as HTMLVideoElement)?.play();
        startAnimation();
      }
    },
  });

  useEffect(() => {
    const handleOnCanPlayThrough = () => setVideoLoaded(true);
    videoRef.current?.addEventListener('oncanplaythrough', handleOnCanPlayThrough);

    return () => videoRef.current?.removeEventListener('oncanplaythrough', handleOnCanPlayThrough);
  }, [videoRef]);

  useEffect(() => {
    if (inView && animationState === 'notStarted') {
      (document.getElementById('cardAnimation') as HTMLVideoElement)?.play();
      startAnimation();
    }
  }, [inView, animationState, videoLoaded]);

  return (
    <Section className="bg-primary-500 pb-64 pt-40 text-white md:pb-96 md:pt-64">
      <Container className={twMerge(clsx('px-32 xs:px-0', animationState === 'playing' && 'overflow-visible'))}>
        <div className="mb-40 md:mb-48 [&>*]:text-center md:[&>*]:mx-auto [&>p]:text-sm md:[&>p]:w-2/5 md:[&>p]:text-md">
          <CustomPortableText
            headingStyles={{
              h1: {
                className: '!text-wrap last-of-type:mb-24 text-secondary-75 [&_strong]:text-secondary-50',
                size: '2xl',
              },
            }}
            content={heading}
          />
        </div>
        <GetKOHOBtn
          className="block w-full pb-40 md:mx-auto md:w-fit md:pb-64"
          buttonClassName="w-full"
          buttonVariant="primaryInverse"
          buttonLocation={trackingParams.search.af_adset_id ?? 'Hero'}
          queryParams={trackingParams}
          trackEventProps={{
            category: 'www page Home',
            label: 'Hero',
            locale,
            location: `${trackingParams.search.af_adset_id ?? 'Hero'}`,
          }}
        >
          {ctaText}
        </GetKOHOBtn>
        <div className="relative mx-auto min-h-560 max-w-800 overflow-visible">
          {animationMp4 && animationWebm && (
            <div
              ref={ref}
              className={twMerge(
                clsx(
                  animationState !== 'notStarted' && 'absolute md:animate-heroSlideAcross md:[animation-delay:2.5s]',
                  '-top-36 -mx-32 !h-440 w-fit min-[524px]:left-0 xs:left-[4.5%] xs:mx-auto sm:left-[16.5%] md:-top-53 md:left-[8%] md:!h-600',
                ),
              )}
            >
              <video
                ref={videoRef}
                id="cardAnimation"
                className={twMerge(
                  clsx(
                    '!h-440 overflow-visible !object-cover object-center opacity-100 transition-opacity duration-[1s] md:!h-600',
                    animationState === 'stopped' && 'opacity-0',
                    !videoLoaded &&
                      animationState === 'notStarted' &&
                      '!h-440 bg-[url(images/initial-card.webp)] bg-contain bg-center bg-no-repeat md:!h-600',
                  ),
                )}
                muted
                onEnded={stopAnimation}
                playsInline
                webkit-playsinline="true"
              >
                {isSafari === null ? null : isSafari ? (
                  <source
                    src={isMobile && animationMp4Mobile ? animationMp4Mobile.asset.url : animationMp4.asset.url}
                    type="video/mp4"
                  />
                ) : (
                  <source
                    src={isMobile && animationWebmMobile ? animationWebmMobile.asset.url : animationWebm.asset.url}
                    type="video/webm"
                  />
                )}
              </video>
            </div>
          )}
          <HeroCarousel
            isSafari={isSafari}
            carousel={carousel}
            animationDone={animationState === 'stopped'}
            className={twMerge(
              clsx('opacity-0 transition-opacity duration-[1s]', animationState === 'stopped' && 'z-2 opacity-100'),
            )}
          />
        </div>
      </Container>
    </Section>
  );
};

export default Hero;
